import ReactMarkdown from "react-markdown";
import React, { useEffect, useState } from "react";

import remarkGfm from "remark-gfm";
import { CopyRegular, DocumentAddRegular } from "@fluentui/react-icons";
import { Button, makeStyles, Tooltip } from "@fluentui/react-components";

import { CqWord } from "@/utils/word";
import { Logo } from "@/components/Logo";
import { translate } from "@/utils/translations";
import { copyToClipboard } from "@/utils/clipboard";
import { markdownToPlainText, markdownToWordHtml } from "@/utils/markdown";

import { useMessageListStyles } from "./styles";

interface AssistantMessageProps {
  content: string;
  typing?: boolean;
  onTypingComplete?: () => void;
  onTypingProgress?: () => void;
}

const AssistantMessage: React.FC<AssistantMessageProps> = ({
  content,
  onTypingComplete,
  onTypingProgress,
  typing: _typing,
}) => {
  const styles = useMessageListStyles();
  const buttonStyles = useButtonStyles();
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);

  // Mock typing completion since the typewriter is commented out
  useEffect(() => {
    if (onTypingProgress) {
      onTypingProgress();
    }
    if (onTypingComplete) {
      onTypingComplete();
    }
  }, [onTypingComplete, onTypingProgress]);

  const handleCopy = async () => {
    try {
      // Use plain text version for clipboard
      const plainText = markdownToPlainText(content);
      await copyToClipboard(plainText);
      setShowCopiedTooltip(true);
      setTimeout(() => setShowCopiedTooltip(false), 2000);
    } catch (error) {
      console.error("Failed to copy content:", error);
    }
  };

  const handleInsert = () => {
    try {
      // Convert markdown to Word-compatible HTML before inserting
      const htmlContent = markdownToWordHtml(content);
      CqWord.addHtmlText(htmlContent, "Start");
    } catch (error) {
      console.error("Failed to insert content:", error);
    }
  };

  return (
    <div className={styles.assistantMessage}>
      <div className={styles.avatar}>
        <Logo size={18} withoutText />
        <span className={styles.avatarText}>Assistant</span>
      </div>
      <div className={styles.assistantMessageBubble}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
        {content && (
          <div className={buttonStyles.buttonContainer}>
            <Button
              appearance="transparent"
              className={buttonStyles.actionButton}
              icon={<DocumentAddRegular />}
              onClick={handleInsert}
              size="small"
            >
              {translate("Insert")}
            </Button>
            <Tooltip
              content={showCopiedTooltip ? "Copied!" : "Copy"}
              relationship="label"
              visible={showCopiedTooltip}
            >
              <Button
                appearance="transparent"
                className={buttonStyles.actionButton}
                icon={<CopyRegular />}
                onClick={handleCopy}
                size="small"
              >
                {translate("Copy")}
              </Button>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssistantMessage;

const useButtonStyles = makeStyles({
  actionButton: {
    fontSize: "12px",
    height: "28px",
    minWidth: "auto",
    paddingLeft: "8px",
    paddingRight: "8px",
  },
  buttonContainer: {
    display: "flex",
    gap: "8px",
    justifyContent: "flex-start",
    marginTop: "8px",
  },
});
